import React, { ReactNode } from 'react'
import { Grid, MenuItem } from '@material-ui/core'
import { useIntl } from 'react-intl'
import LiquidSelect from '@src/components/LiquidSelect'
import LiquidFormLabel from '@src/components/LiquidFormLabel'
import { useProductFromQueryParam } from '../../ProductsConfiguration'
import useFormikHelper from '../../useFormikHelper'
import { ModificationTypes } from '../../enum'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { useModificationAndSpecificationsQuery } from '@src/queries/ConfiguratorModificationsAndSpecifications.generated'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import { splitMessages } from '../../utils'
import useTubeValidation from '../../useTubeValidation'

interface OverHangModificationsProps {
  disabled: boolean
  name: string
  label?: ReactNode
  categoryValue?: string
}

const OverHangModifications: React.FC<OverHangModificationsProps> = ({
  disabled,
  name,
  label,
  categoryValue = '',
}) => {
  const intl = useIntl()
  const product = useProductFromQueryParam()
  const formik = useFormikHelper()
  const { validationAPI } = useTubeValidation()
  const { isCustomerServiceRep, isCustomProductCSR, userIsLoggedIn } =
    useCurrentUser()
  const { data } = useModificationAndSpecificationsQuery({
    variables: {
      product: {
        productType: product,
        isCSR: isCustomerServiceRep,
        isCSRSupervisor: isCustomProductCSR,
        isRegUser: userIsLoggedIn,
      },
    },
    ssr: false,
    fetchPolicy: 'cache-first',
  })

  const modifications =
    data?.getModificationAndSpecifications?.modifications?.find(
      (mod) => mod.modificationType === ModificationTypes.Overhang
    )?.category || []

  const allModifications = modifications
    .filter((category) =>
      categoryValue && categoryValue !== ''
        ? category.categoryName === categoryValue
        : true
    )
    .flatMap((single) => single.modifications)

  const modificationList = [
    {
      displayName: intl.formatMessage({
        id: 'PC_SPEC_SELECT_antisera',
        defaultMessage: 'Select',
      }),
      order: 0,
      value: '',
    },
  ].concat(
    allModifications
      .map((modification) => ({
        displayName: intl.formatMessage({
          id: modification.displayName,
          defaultMessage: modification.name,
        }),
        order: modification.order ? parseInt(modification.order) : 0,
        value: modification.value,
      }))
      .sort((a, b) => a.order - b.order)
  )

  const onModificationChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    formik.setOligoFieldValue(name, e.target.value, false)
    formik.setFieldTouched(name, true, false)
    formik.setFieldTouched('sequence', true, false)
    if (
      name === 'customerOverhang' &&
      formik.selectedOligoValues?.autoGenerateOligo
    ) {
      formik.setOligoFieldValue(
        'customerAntisenseOverHang',
        e.target.value,
        false
      )
      validationAPI(
        {
          ...formik.validationFields,
          customerOverhang: e.target.value as string,
          customerAntisenseOverHang: e.target.value as string,
        },
        true
      )
    } else {
      validationAPI(
        { ...formik.validationFields, [name]: e.target.value },
        true
      )
    }
  }
  const errors =
    (!formik.allValues?.loading &&
      formik.errors?.[name] &&
      (formik.touched?.[name] ||
        formik.allValues?.hasTriedNextStep ||
        formik.allValues?.oligoList?.[formik.allValues.activeOligoIndex]
          ?.autoGenerateOligo) &&
      splitMessages(formik.errors[name])) ||
    []

  return (
    <Grid container>
      <Grid item xs={12}>
        {label && (
          <LiquidFormLabel htmlFor={name} required error={errors?.length > 0}>
            {label}
          </LiquidFormLabel>
        )}
        <LiquidSelect
          onChange={onModificationChange}
          value={formik.selectedOligoValues[name]}
          displayEmpty
          disabled={disabled}
          error={errors?.length > 0}
        >
          {modificationList.map((mod) => (
            <MenuItem key={mod?.value} value={mod?.value}>
              {mod.displayName}
            </MenuItem>
          ))}
        </LiquidSelect>
      </Grid>
      {errors?.length > 0 && (
        <Grid>
          <ValidationErrors errors={errors} configuratorsRedesign />
        </Grid>
      )}
    </Grid>
  )
}

export default OverHangModifications
